import ComparableAnalysesPage from "@/pages/ComparableAnalysesPage.vue"
import ComparableAnalysisPage from "@/pages/ComparableAnalysisPage.vue"
import HomePage from "@/pages/HomePage.vue"
import HuntingPage from "@/pages/HuntingPage.vue"
import MorePage from "@/pages/MorePage.vue"
import LoginPage from "@/pages/LoginPage.vue"
import PermissionPage from "@/pages/PermissionPage.vue"
import AuthCallbackPage from "./pages/AuthCallbackPage.vue"
import AdminPanelPage from "@/pages/AdminPanelPage.vue"
import AdminLayout from "@/components/admin/AdminLayout.vue"
import EmailTemplatesPage from "@/pages/EmailTemplatesPage.vue"
import ServiceTokensPage from "@/pages/ServiceTokensPage.vue"
import MyProfilePage from "@/pages/MyProfilePage.vue"
import SettingsPage from "@/pages/SettingsPage.vue"
import ActivityPage from "@/pages/ActivityPage.vue"
import ContactResearchPage from "@/pages/ContactResearchPage.vue"
import { UseRootStore, isTableType } from "@/model/RootStore"
import { createRouter, createWebHistory } from "vue-router"
import { appVersion } from "./AppConfig"
import {
	Route,
	firstQueryValue,
	replaceSelectionQueryParam,
	SelectionQueryParamKeys,
	type SelectionQueryParamKey,
} from "@/utils/routing"
import { storeToRefs } from "pinia"
import { watch } from "vue"

console.info("App version: ", appVersion)

type QueryParamOpportunity = {
	id: string | number
	hubspot_record_id?: string | null
}

type ActiveSelectionQueryParam = {
	key: SelectionQueryParamKey
	value: string
}

// Selection query params (?transaction, ?parcel, ?opp) and map/card selection
// state update each other. Both directions must be idempotent because each can
// trigger the other: route -> search intent -> selection -> route.
//
// A route-originated search can temporarily clear selection before the target is
// selected on the map, so keep track of the query param currently being replayed
// and ignore those transient clears until the matching selection appears.
let pendingQueryParamReplay: ActiveSelectionQueryParam | null = null

const getRoutes = () => {
	const routes = [
		{ path: "/comparable-analyses", name: Route.ComparableAnalyses, component: ComparableAnalysesPage },
		{ path: "/comparable-analyses/:id", name: Route.ComparableAnalysis, component: ComparableAnalysisPage },
		{
			path: "/",
			name: Route.Home,
			component: HomePage,
		},
		{
			path: "/hunting",
			name: Route.Hunting,
			component: HuntingPage,
		},
		{
			path: "/more",
			name: Route.More,
			component: MorePage,
		},
		{
			path: "/login",
			name: Route.Login,
			components: { default: HomePage, overlay: LoginPage },
		},
		{
			path: "/logout",
			name: "logout",
			components: { default: HomePage, overlay: LoginPage },
		},
		{
			path: "/admin",
			name: Route.Admin,
			component: AdminLayout,
			children: [
				{
					path: "/admin-panel",
					name: Route.AdminUsers,
					component: AdminPanelPage,
				},
				{
					path: "/permission",
					name: Route.AdminPermissions,
					component: PermissionPage,
				},
				{
					path: "/admin/email-templates",
					name: Route.AdminEmailTemplates,
					component: EmailTemplatesPage,
				},
				{
					path: "/admin/service-tokens",
					name: Route.AdminServiceTokens,
					component: ServiceTokensPage,
				},
			],
		},
		{
			path: "/settings",
			name: "settings",
			component: SettingsPage,
		},
		{
			path: "/role-guide",
			redirect: { path: "/permission" },
		},
		{
			path: "/permission-matrix",
			redirect: { path: "/permission" },
		},
		{
			path: "/auth/callback",
			name: "auth-callback",
			component: AuthCallbackPage,
		},
		{
			path: "/my-profile",
			name: "my-profile",
			component: MyProfilePage,
		},
		{
			path: "/notifications",
			redirect: { path: "/activity" },
		},
		{
			path: "/contact-research",
			name: Route.ContactResearch,
			component: ContactResearchPage,
		},
	]

	routes.push({
		path: "/activity",
		name: Route.Activity,
		component: ActivityPage,
	})

	return routes
}

const AppRouter = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: getRoutes(),
})

function handleQueryParams(query: typeof AppRouter.currentRoute.value.query): void {
	const store = UseRootStore()
	const selectionQueryParam = firstSelectionQueryParam(query)

	// Only one selection query param is handled at a time. This avoids multiple
	// map jumps when old params are still present in copied URLs.
	if (selectionQueryParam?.key === "transaction") {
		store.searches.parcel = undefined
		store.searches.opportunity = undefined
		if (
			store.selectedTransaction?.inscriptionNumber !== selectionQueryParam.value &&
			store.searches.transaction !== selectionQueryParam.value
		) {
			pendingQueryParamReplay = selectionQueryParam
			store.searches.transaction = selectionQueryParam.value
		}
		return
	}

	store.searches.transaction = undefined

	if (selectionQueryParam?.key === "parcel") {
		store.searches.opportunity = undefined
		if (store.selectedParcelID !== selectionQueryParam.value && store.searches.parcel !== selectionQueryParam.value) {
			pendingQueryParamReplay = selectionQueryParam
			store.searches.parcel = selectionQueryParam.value
		}
		return
	}

	store.searches.parcel = undefined

	if (selectionQueryParam?.key === "opp") {
		const selectedOpportunity = findOpportunityByQueryParamId(
			store.opportunities as QueryParamOpportunity[],
			store.selectedOpportunityId
		)
		if (
			selectedOpportunity?.hubspot_record_id !== selectionQueryParam.value &&
			store.searches.opportunity !== selectionQueryParam.value
		) {
			pendingQueryParamReplay = selectionQueryParam
			store.searches.opportunity = selectionQueryParam.value
		}
		return
	}

	store.searches.opportunity = undefined
	pendingQueryParamReplay = null
}

function firstSelectionQueryParam(query: typeof AppRouter.currentRoute.value.query): ActiveSelectionQueryParam | null {
	for (const key of SelectionQueryParamKeys) {
		const value = firstQueryValue(query[key])
		if (value) return { key, value }
	}

	return null
}

function isPendingQueryParamClear(key: SelectionQueryParamKey): boolean {
	if (pendingQueryParamReplay?.key !== key) return false

	return firstQueryValue(AppRouter.currentRoute.value.query[key]) === pendingQueryParamReplay.value
}

function clearPendingQueryParamSelection(key: SelectionQueryParamKey, value: string | null | undefined): void {
	if (value && pendingQueryParamReplay?.key === key && pendingQueryParamReplay.value === value) {
		pendingQueryParamReplay = null
	}
}

function findOpportunityByQueryParamId(
	opportunities: QueryParamOpportunity[],
	opportunityId: string | number | null | undefined
): QueryParamOpportunity | undefined {
	if (opportunityId == null) return undefined

	const id = String(opportunityId)
	return opportunities.find(opportunity => String(opportunity.id) === id || opportunity.hubspot_record_id === id)
}

function watchSelectionQueryParams(): void {
	const store = UseRootStore()
	const { opportunities, selectedOpportunityId, selectedParcelID, selectedTransaction } = storeToRefs(store)

	watch(selectedTransaction, transaction => {
		const inscriptionNumber = transaction?.inscriptionNumber
		clearPendingQueryParamSelection("transaction", inscriptionNumber)
		// Ignore transient clears caused while fulfilling a route-originated
		// transaction search; otherwise the URL can erase itself mid-selection.
		if (!inscriptionNumber && isPendingQueryParamClear("transaction")) return

		replaceSelectionQueryParam(AppRouter, "transaction", inscriptionNumber)
	})

	watch(selectedParcelID, parcelId => {
		clearPendingQueryParamSelection("parcel", parcelId)
		// Parcel search clears existing map selection before selecting the target
		// feature, so do not remove ?parcel until the route command resolves.
		if (!parcelId && isPendingQueryParamClear("parcel")) return

		replaceSelectionQueryParam(AppRouter, "parcel", parcelId)
	})

	watch(
		() => [selectedOpportunityId.value, opportunities.value] as const,
		([opportunityId, currentOpportunities]) => {
			if (!opportunityId) {
				// Opportunity searches can also clear selection before the matching
				// opportunity is available/selected.
				if (isPendingQueryParamClear("opp")) return

				replaceSelectionQueryParam(AppRouter, "opp", undefined)
				return
			}

			const opportunity = findOpportunityByQueryParamId(currentOpportunities as QueryParamOpportunity[], opportunityId)
			const hubspotRecordId = opportunity?.hubspot_record_id
			clearPendingQueryParamSelection("opp", hubspotRecordId)
			if (!hubspotRecordId && isPendingQueryParamClear("opp")) return

			replaceSelectionQueryParam(AppRouter, "opp", hubspotRecordId)
		}
	)
}

void AppRouter.isReady().then(() => {
	handleQueryParams(AppRouter.currentRoute.value.query)
	watchSelectionQueryParams()
})

// selection query params
AppRouter.afterEach(to => {
	handleQueryParams(to.query)
})

// Persist the last table the user navigated to so it can be restored as the
// default. The table view itself is derived from the ?table query param.
AppRouter.afterEach((to, from) => {
	if (to.query.table === from.query.table) return

	const tableQuery = firstQueryValue(to.query.table)
	if (isTableType(tableQuery)) {
		UseRootStore().storeTableType(tableQuery)
	}
})

export default AppRouter
